import {
  Component,
  computed,
  inject,
  Input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  ClienteService,
  BardoContextDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { BottomSheetComponent, DeviceService } from '@nomoh-monorepo/shared-common-ui';
import { IEstabelecimentoClienteEntity } from '@nomoh-monorepo/shared-types';
import { formatarTelefone } from '@nomoh-monorepo/shared-util-tools';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClienteHistoricoComponent } from '../cliente-historico/cliente-historico.component';


@Component({
  selector: 'lib-cliente-info',
  standalone: true,
  templateUrl: './cliente-info.component.html',
  styleUrls: ['./cliente-info.component.scss'],
  imports: [CommonModule, ButtonModule, DialogModule, BottomSheetComponent, ClienteHistoricoComponent, TranslateModule],
})
export class ClienteInfoComponent implements OnChanges {
  @Input() cliente: IEstabelecimentoClienteEntity =
    {} as IEstabelecimentoClienteEntity;
  modalExclusaoAberto = signal(false);
  modalHistoricoAberto = signal(false);
  excluindo = signal(false);
  exibirTodosServicos = signal(false);
  closed = output<void>();
  verHistorico = output<void>();

  abrirHistoricoModal() {
    this.modalHistoricoAberto.set(true);
  }


  private clienteService = inject(ClienteService);

  private confirmationService = inject(ConfirmationService);
  deviceService = inject(DeviceService);
  private contextoService = inject(BardoContextDataService);
  private translateService = inject(TranslateService);
  $nicho = inject(NichoTermosService).$nicho;

  possuiPermissao(permissao: string) {
    return this.contextoService.possuiPermissao(permissao);
  }

  private avatarPalette = [
    '#F97316',
    '#6D28D9',
    '#2563EB',
    '#0EA5E9',
    '#059669',
    '#EA580C',
  ];

  metricas = {
    cancelados: 0,
    atendidos: 0,
    confirmados: 0,
    servicosMaisUtilizados: [] as Array<{ nome: string; count: number }>,
  };

  carregandoMetricas = signal(false);

  servicosLista = computed(() => {
    const todos = this.metricas.servicosMaisUtilizados || [];
    if (this.exibirTodosServicos()) {
      return todos;
    }
    return todos.slice(0, 3);
  });

  hasMaisServicos = computed(() => {
    return (this.metricas.servicosMaisUtilizados?.length || 0) > 3;
  });

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['cliente']?.currentValue?.id) {
      this.metricas = {
        cancelados: 0,
        atendidos: 0,
        confirmados: 0,
        servicosMaisUtilizados: [],
      };
      this.carregandoMetricas.set(true);
      this.exibirTodosServicos.set(false);
      try {
        this.metricas = await this.clienteService.buscarMetricas(
          this.cliente.id as string
        );
      } finally {
        this.carregandoMetricas.set(false);
      }
    }
  }

  getAvatarColor(id?: string): string {
    if (!id) return this.avatarPalette[0];
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    return this.avatarPalette[Math.abs(hash) % this.avatarPalette.length];
  }

  getIniciais(nome?: string | null): string {
    if (!nome) return '';
    return nome
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  formatarZeroEsquerda(valor: number): string {
    if (valor === undefined || valor === null) return '00';
    return valor < 10 ? `0${valor}` : `${valor}`;
  }

  formatarCount(count: number): string {
    if (count === undefined || count === null) return '00x';
    const num = count < 10 ? `0${count}` : `${count}`;
    return `${num}x`;
  }

  toggleExibirTodosServicos() {
    this.exibirTodosServicos.update((v) => !v);
  }

  confirmarExclusao(cliente: IEstabelecimentoClienteEntity, event: Event) {
    event.stopPropagation();
    if (this.deviceService.$isMobile()) {
      this.modalExclusaoAberto.set(true);
    } else {
      this.confirmationService.confirm({
        target: event.target as EventTarget,
        message: this.translateService.instant('BARDO_CLIENTES.CONFIRMAR_EXCLUSAO_MSG', {
          termo: this.$nicho().cliente.singular.toLowerCase(),
          nome: cliente.nome,
        }),
        header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.excluirCliente(cliente.id as string),
      });
    }
  }

  async excluirCliente(id?: string) {
    if (!id) return;
    this.excluindo.set(true);
    try {
      await this.clienteService.deletar(id);
      this.modalExclusaoAberto.set(false);
      this.closed.emit();
    } catch (error) {
      console.error('Erro ao excluir cliente', error);
    } finally {
      this.excluindo.set(false);
    }
  }

  formatarTelefone(telefone?: string | null): string {
    if (!telefone) {
      return this.translateService.instant('BARDO_CLIENTES.NAO_INFORMADO') || 'Não informado';
    }
    return formatarTelefone(telefone);
  }
}

